/**
 * VendorDetail — Vendor directory with detail panel
 * Inspired by Zoho Books: Left list + Right panel (Overview, Transactions, Statement tabs)
 * Includes: Performance Score, Edit dialog, New Transaction dropdown, More menu
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import {
  Search, User, Building2, Phone, Mail, FileText, DollarSign,
  Download, BarChart3, Activity, CreditCard, ArrowUpRight, Briefcase,
  Pencil, Paperclip, Plus, ChevronDown, MoreHorizontal, Trash2,
  Ban, Send, ShoppingCart, TrendingUp, Target, Award, Zap
} from 'lucide-react';
import Layout from '@/components/Layout';
import EmailComposeDialog from '@/components/EmailComposeDialog';
import { formatDate } from '@/utils/dateFormat';

// ─── Left Panel: Vendor List ────────────────────────────────────────────
function VendorList({ vendors, selectedId, onSelect, searchQuery, setSearchQuery, onCompare }) {
  const filtered = useMemo(() => {
    if (!searchQuery) return vendors;
    const q = searchQuery.toLowerCase();
    return vendors.filter(v =>
      (v.name || '').toLowerCase().includes(q) ||
      (v.email || '').toLowerCase().includes(q) ||
      (v.company_name || '').toLowerCase().includes(q)
    );
  }, [vendors, searchQuery]);

  return (
    <div className="w-[300px] min-w-[300px] bg-white border-r border-stone-200 flex flex-col h-[calc(100vh-64px)]" data-testid="vendor-list-panel">
      <div className="p-3 border-b border-stone-200">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Search vendors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="vendor-search-input"
          />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <p className="text-[10px] text-stone-400">{filtered.length} vendors</p>
          <button onClick={onCompare} className="text-[10px] text-blue-600 hover:text-blue-800 font-medium" data-testid="compare-vendors-btn">
            Compare
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.map(v => (
          <div
            key={v.id}
            onClick={() => onSelect(v.id)}
            className={`px-3 py-3 cursor-pointer border-b border-stone-100 transition-colors ${
              selectedId === v.id
                ? 'bg-stone-900 text-white'
                : 'hover:bg-stone-50'
            }`}
            data-testid={`vendor-item-${v.id}`}
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className={`font-medium text-sm truncate ${selectedId === v.id ? 'text-white' : 'text-stone-900'}`}>
                  {v.name}
                </p>
                {v.company_name && (
                  <p className={`text-xs truncate ${selectedId === v.id ? 'text-stone-300' : 'text-stone-500'}`}>
                    {v.company_name}
                  </p>
                )}
              </div>
              <div className="text-right ml-2 flex-shrink-0">
                {v.total_paid > 0 && (
                  <p className={`text-xs font-semibold ${selectedId === v.id ? 'text-green-300' : 'text-stone-700'}`}>
                    {'\u20B9'}{v.total_paid.toLocaleString('en-IN')}
                  </p>
                )}
                <p className={`text-[10px] ${selectedId === v.id ? 'text-stone-400' : 'text-stone-400'}`}>
                  {v.projects_count} project{v.projects_count !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-stone-400">No vendors found</div>
        )}
      </div>
    </div>
  );
}

// ─── Performance Score Card ─────────────────────────────────────────────
function PerformanceScoreCard({ score }) {
  if (!score) return null;
  const { total, grade, breakdown } = score;
  const gradeColors = { A: 'text-green-600 bg-green-50 border-green-200', B: 'text-blue-600 bg-blue-50 border-blue-200', C: 'text-amber-600 bg-amber-50 border-amber-200', D: 'text-red-600 bg-red-50 border-red-200' };
  const barColor = grade === 'A' ? 'bg-green-500' : grade === 'B' ? 'bg-blue-500' : grade === 'C' ? 'bg-amber-500' : 'bg-red-400';
  const items = [
    { key: 'payment_volume', icon: DollarSign, label: 'Payment Volume' },
    { key: 'project_breadth', icon: Briefcase, label: 'Project Breadth' },
    { key: 'quote_engagement', icon: FileText, label: 'Quote Engagement' },
    { key: 'consistency', icon: TrendingUp, label: 'Consistency' },
  ];

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4" data-testid="performance-score-card">
      <h3 className="font-semibold text-stone-800 text-sm mb-3 flex items-center gap-2">
        <Award size={14} /> Vendor Performance Score
      </h3>
      <div className="flex items-center gap-4 mb-4">
        {/* Score Circle */}
        <div className="relative w-20 h-20 flex-shrink-0">
          <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
            <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none" stroke="#e7e5e4" strokeWidth="3" />
            <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none" stroke={grade === 'A' ? '#22c55e' : grade === 'B' ? '#3b82f6' : grade === 'C' ? '#f59e0b' : '#ef4444'}
              strokeWidth="3" strokeDasharray={`${total}, 100`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-lg font-bold text-stone-900">{total}</span>
            <span className="text-[9px] text-stone-500">/100</span>
          </div>
        </div>
        {/* Grade Badge */}
        <div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-lg font-bold border ${gradeColors[grade] || gradeColors.D}`}>
            Grade {grade}
          </span>
          <p className="text-xs text-stone-500 mt-1.5">
            {total >= 80 ? 'Excellent vendor' : total >= 60 ? 'Good performance' : total >= 40 ? 'Average engagement' : 'Needs more activity'}
          </p>
        </div>
      </div>
      {/* Breakdown */}
      <div className="space-y-2.5">
        {items.map(({ key, icon: Icon, label }) => {
          const b = breakdown[key];
          if (!b) return null;
          const pct = Math.round((b.score / b.max) * 100);
          return (
            <div key={key} className="flex items-center gap-2">
              <Icon size={12} className="text-stone-400 flex-shrink-0" />
              <span className="text-xs text-stone-600 w-28 flex-shrink-0">{label}</span>
              <div className="flex-1 bg-stone-100 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
              </div>
              <span className="text-[10px] text-stone-500 w-16 text-right">{b.score}/{b.max} - {b.detail}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Edit Vendor Dialog ─────────────────────────────────────────────────
const SALUTATIONS_V = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'];
const GST_TREATMENTS_V = ['Registered Business', 'Unregistered Business', 'Consumer', 'Overseas', 'SEZ', 'Deemed Export'];
const INDIAN_STATES_V = ['[AN] - Andaman & Nicobar', '[AP] - Andhra Pradesh', '[AR] - Arunachal Pradesh', '[AS] - Assam', '[BR] - Bihar', '[CG] - Chhattisgarh', '[CH] - Chandigarh', '[DD] - Daman & Diu', '[DL] - Delhi', '[GA] - Goa', '[GJ] - Gujarat', '[HP] - Himachal Pradesh', '[HR] - Haryana', '[JH] - Jharkhand', '[JK] - Jammu & Kashmir', '[KA] - Karnataka', '[KL] - Kerala', '[LA] - Ladakh', '[LD] - Lakshadweep', '[MH] - Maharashtra', '[ML] - Meghalaya', '[MN] - Manipur', '[MP] - Madhya Pradesh', '[MZ] - Mizoram', '[NL] - Nagaland', '[OD] - Odisha', '[PB] - Punjab', '[PY] - Puducherry', '[RJ] - Rajasthan', '[SK] - Sikkim', '[TN] - Tamil Nadu', '[TS] - Telangana', '[TR] - Tripura', '[UK] - Uttarakhand', '[UP] - Uttar Pradesh', '[WB] - West Bengal'];
const PAYMENT_TERMS_V = ['Due on Receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60', '50% Advance', 'Custom'];
const CURRENCIES_V = ['INR - Indian Rupees', 'USD - US Dollar', 'EUR - Euro', 'GBP - British Pound'];
const TDS_OPTIONS = ['Select a Tax', 'TDS - Section 194C (1%)', 'TDS - Section 194C (2%)', 'TDS - Section 194J (10%)', 'TDS - Section 194I (10%)', 'TDS - Section 194H (5%)', 'No TDS'];

function EditVendorDialog({ open, onClose, vendor, onSaved }) {
  const token = localStorage.getItem('token');
  const [activeTab, setActiveTab] = useState('other_details');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (vendor) {
      setForm({
        salutation: vendor.salutation || '',
        first_name: vendor.first_name || '',
        last_name: vendor.last_name || '',
        company_name: vendor.company_name || '',
        display_name: vendor.display_name || vendor.name || '',
        email: vendor.email || '',
        work_phone: vendor.work_phone || '',
        mobile: vendor.mobile || vendor.phone || '',
        communication_channels: vendor.communication_channels || ['email'],
        // Other Details
        gst_treatment: vendor.gst_treatment || 'Unregistered Business',
        source_of_supply: vendor.source_of_supply || vendor.place_of_supply || '[MH] - Maharashtra',
        pan_number: vendor.pan_number || '',
        gst_number: vendor.gst_number || '',
        msme_registered: vendor.msme_registered || false,
        currency: vendor.currency || 'INR - Indian Rupees',
        payment_terms: vendor.payment_terms || 'Due on Receipt',
        tds: vendor.tds || 'Select a Tax',
        // Address
        billing_address: vendor.billing_address || vendor.address || '',
        billing_city: vendor.billing_city || '',
        billing_state: vendor.billing_state || '',
        billing_pincode: vendor.billing_pincode || '',
        billing_country: vendor.billing_country || 'India',
        // Bank
        bank_name: vendor.bank_name || '',
        bank_account_number: vendor.bank_account_number || '',
        bank_ifsc: vendor.bank_ifsc || '',
        bank_branch: vendor.bank_branch || '',
        // Remarks
        remarks: vendor.remarks || vendor.notes || '',
      });
    }
  }, [vendor]);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const toggleChannel = (ch) => {
    const chs = form.communication_channels?.includes(ch)
      ? form.communication_channels.filter(c => c !== ch)
      : [...(form.communication_channels || []), ch];
    set('communication_channels', chs);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, name: form.display_name || `${form.first_name} ${form.last_name}`.trim() };
      await axios.put(`${API}/vendors/${vendor.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendor updated');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update vendor');
    } finally { setSaving(false); }
  };

  const tabs = [
    { id: 'other_details', label: 'Other Details' },
    { id: 'address', label: 'Address' },
    { id: 'bank_details', label: 'Bank Details' },
    { id: 'remarks', label: 'Remarks' },
  ];

  const FieldRow = ({ label, required, children }) => (
    <div className="grid grid-cols-[130px_1fr] items-center gap-x-4">
      <Label className={`text-sm text-right ${required ? 'text-red-600 font-semibold' : 'text-stone-600'}`}>{label}{required ? ' *' : ''}</Label>
      {children}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl w-[90vw] max-h-[90vh] overflow-y-auto" data-testid="edit-vendor-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">Edit Vendor</DialogTitle>
          <p className="text-sm text-stone-500">Update vendor contact and business details</p>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Primary Contact */}
          <FieldRow label="Primary Contact">
            <div className="flex gap-2">
              <Select value={form.salutation} onValueChange={(v) => set('salutation', v)}>
                <SelectTrigger className="w-28"><SelectValue placeholder="Salutation" /></SelectTrigger>
                <SelectContent>{SALUTATIONS_V.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={form.first_name || ''} onChange={(e) => set('first_name', e.target.value)} placeholder="First Name" className="flex-1" data-testid="edit-vendor-first-name" />
              <Input value={form.last_name || ''} onChange={(e) => set('last_name', e.target.value)} placeholder="Last Name" className="flex-1" data-testid="edit-vendor-last-name" />
            </div>
          </FieldRow>
          <FieldRow label="Company Name">
            <Input value={form.company_name || ''} onChange={(e) => set('company_name', e.target.value)} className="max-w-sm" data-testid="edit-vendor-company" />
          </FieldRow>
          <FieldRow label="Display Name" required>
            <Input required value={form.display_name || ''} onChange={(e) => set('display_name', e.target.value)} className="max-w-sm" data-testid="edit-vendor-display-name" />
          </FieldRow>
          <FieldRow label="Email Address">
            <Input type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} className="max-w-sm" data-testid="edit-vendor-email" />
          </FieldRow>
          <div className="grid grid-cols-[130px_1fr] items-start gap-x-4">
            <Label className="text-sm text-stone-600 text-right pt-2">Phone</Label>
            <div className="flex gap-2 flex-wrap">
              <div className="flex gap-1 items-center">
                <span className="text-xs text-stone-400 bg-stone-100 px-2 py-1.5 rounded border">+91</span>
                <Input value={form.work_phone || ''} onChange={(e) => set('work_phone', e.target.value)} placeholder="Work Phone" className="w-36" data-testid="edit-vendor-work-phone" />
              </div>
              <div className="flex gap-1 items-center">
                <span className="text-xs text-stone-400 bg-stone-100 px-2 py-1.5 rounded border">+91</span>
                <Input value={form.mobile || ''} onChange={(e) => set('mobile', e.target.value)} placeholder="Mobile" className="w-36" data-testid="edit-vendor-mobile" />
              </div>
            </div>
          </div>
          <FieldRow label="Communication">
            <div className="flex gap-4">
              {['email', 'whatsapp'].map(ch => (
                <label key={ch} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.communication_channels?.includes(ch)} onChange={() => toggleChannel(ch)} className="accent-stone-800 w-4 h-4" />
                  <span className="text-sm capitalize">{ch === 'whatsapp' ? 'WhatsApp' : 'Email'}</span>
                </label>
              ))}
            </div>
          </FieldRow>

          {/* Tabs */}
          <div className="border-b border-stone-200 mt-4">
            <div className="flex gap-0">
              {tabs.map(tab => (
                <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-stone-900 text-stone-900' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
                >{tab.label}</button>
              ))}
            </div>
          </div>

          {activeTab === 'other_details' && (
            <div className="space-y-5">
              <FieldRow label="GST Treatment" required>
                <Select value={form.gst_treatment} onValueChange={(v) => set('gst_treatment', v)}>
                  <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{GST_TREATMENTS_V.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </FieldRow>
              {form.gst_treatment === 'Registered Business' && (
                <FieldRow label="GSTIN">
                  <Input value={form.gst_number || ''} onChange={(e) => set('gst_number', e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" className="max-w-sm" data-testid="edit-vendor-gstin" />
                </FieldRow>
              )}
              <FieldRow label="Source of Supply" required>
                <Select value={form.source_of_supply} onValueChange={(v) => set('source_of_supply', v)}>
                  <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{INDIAN_STATES_V.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label="PAN">
                <Input value={form.pan_number || ''} onChange={(e) => set('pan_number', e.target.value.toUpperCase())} placeholder="ABCDE1234F" className="max-w-sm" maxLength={10} data-testid="edit-vendor-pan" />
              </FieldRow>
              <FieldRow label="MSME Registered?">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.msme_registered || false} onChange={(e) => set('msme_registered', e.target.checked)} className="accent-stone-800 w-4 h-4" />
                  <span className="text-sm">This vendor is MSME registered</span>
                </label>
              </FieldRow>
              <FieldRow label="Currency">
                <Select value={form.currency} onValueChange={(v) => set('currency', v)}>
                  <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES_V.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label="Payment Terms">
                <Select value={form.payment_terms} onValueChange={(v) => set('payment_terms', v)}>
                  <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_TERMS_V.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label="TDS">
                <Select value={form.tds} onValueChange={(v) => set('tds', v)}>
                  <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{TDS_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </FieldRow>
            </div>
          )}

          {activeTab === 'address' && (
            <div className="space-y-5">
              <div className="grid grid-cols-[130px_1fr] items-start gap-x-4">
                <Label className="text-sm text-stone-600 text-right pt-2">Address</Label>
                <Textarea value={form.billing_address || ''} onChange={(e) => set('billing_address', e.target.value)} placeholder="Street address, apartment, suite, etc." rows={2} className="max-w-lg" />
              </div>
              <FieldRow label="City">
                <Input value={form.billing_city || ''} onChange={(e) => set('billing_city', e.target.value)} placeholder="City" className="max-w-sm" />
              </FieldRow>
              <FieldRow label="State">
                <Input value={form.billing_state || ''} onChange={(e) => set('billing_state', e.target.value)} placeholder="State" className="max-w-sm" />
              </FieldRow>
              <FieldRow label="PIN Code">
                <Input value={form.billing_pincode || ''} onChange={(e) => set('billing_pincode', e.target.value)} placeholder="PIN Code" className="max-w-xs" maxLength={6} />
              </FieldRow>
              <FieldRow label="Country">
                <Input value={form.billing_country || ''} onChange={(e) => set('billing_country', e.target.value)} className="max-w-sm" />
              </FieldRow>
            </div>
          )}

          {activeTab === 'bank_details' && (
            <div className="space-y-5">
              <FieldRow label="Bank Name">
                <Input value={form.bank_name || ''} onChange={(e) => set('bank_name', e.target.value)} placeholder="e.g., HDFC Bank" className="max-w-sm" data-testid="edit-vendor-bank-name" />
              </FieldRow>
              <FieldRow label="Account Number">
                <Input value={form.bank_account_number || ''} onChange={(e) => set('bank_account_number', e.target.value)} placeholder="Account number" className="max-w-sm" data-testid="edit-vendor-bank-acc" />
              </FieldRow>
              <FieldRow label="IFSC Code">
                <Input value={form.bank_ifsc || ''} onChange={(e) => set('bank_ifsc', e.target.value.toUpperCase())} placeholder="e.g., HDFC0001234" className="max-w-sm" data-testid="edit-vendor-bank-ifsc" />
              </FieldRow>
              <FieldRow label="Branch">
                <Input value={form.bank_branch || ''} onChange={(e) => set('bank_branch', e.target.value)} placeholder="Branch name" className="max-w-sm" />
              </FieldRow>
            </div>
          )}

          {activeTab === 'remarks' && (
            <div className="space-y-5">
              <div className="grid grid-cols-[130px_1fr] items-start gap-x-4">
                <Label className="text-sm text-stone-600 text-right pt-2">Remarks</Label>
                <Textarea value={form.remarks || ''} onChange={(e) => set('remarks', e.target.value)} placeholder="Internal notes about this vendor..." rows={4} className="max-w-lg" data-testid="edit-vendor-remarks" />
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-3 pt-4 border-t border-stone-200">
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-vendor-btn">
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Vendor Email Dialog ────────────────────────────────────────────────
function VendorEmailDialog({ open, onClose, vendor, mode = 'general', statementDates = null }) {
  const token = localStorage.getItem('token');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [attachStatement, setAttachStatement] = useState(false);

  useEffect(() => {
    if (!open || !vendor) return;
    setTo(vendor.email || '');
    setCc('');
    const vendorName = vendor.name || 'Vendor';
    const isStatement = mode === 'statement';
    setAttachStatement(isStatement);
    setSubject(isStatement 
      ? `Vendor Statement - ${vendorName}` 
      : `Regarding - ${vendorName}`
    );
    setBody(isStatement 
      ? `Dear ${vendorName},\n\nPlease find attached your statement of account for your records.\n\nIf you have any questions about the transactions listed, please don't hesitate to reach out.\n\nRegards,\nBella Casa Interiors`
      : `Dear ${vendorName},\n\n\n\nRegards,\nBella Casa Interiors`
    );
  }, [open, vendor, mode]);

  const handleSend = async () => {
    if (!to.trim()) { toast.error('Recipient email is required'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    setSending(true);
    try {
      const payload = {
        to: to.trim(),
        cc: cc.trim(),
        subject: subject.trim(),
        body: body.trim(),
      };
      if (attachStatement && vendor?.id) {
        payload.vendor_id = vendor.id;
        payload.attach_vendor_statement = true;
        payload.statement_from_date = statementDates?.start || '2020-01-01';
        payload.statement_to_date = statementDates?.end || new Date().toISOString().slice(0, 10);
      }
      await axios.post(`${API}/send-composed-email`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(attachStatement ? 'Email sent with vendor statement!' : 'Email sent successfully!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="vendor-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-amber-100"><Mail size={16} className="text-amber-700" /></div>
            {mode === 'statement' ? 'Email Statement' : 'Email Vendor'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div>
            <Label className="text-xs text-stone-500">To *</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="vendor@email.com" data-testid="vendor-email-to" />
          </div>
          <div>
            <Label className="text-xs text-stone-500">CC</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="cc@email.com (comma separated)" data-testid="vendor-email-cc" />
          </div>
          <div>
            <Label className="text-xs text-stone-500">Subject *</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="vendor-email-subject" />
          </div>
          <div>
            <Label className="text-xs text-stone-500">Message</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="text-sm resize-none" data-testid="vendor-email-body" />
          </div>
          {mode === 'statement' && (
            <div className="flex items-center gap-2 bg-blue-50 p-2 rounded-lg border border-blue-100">
              <input type="checkbox" checked={attachStatement} onChange={(e) => setAttachStatement(e.target.checked)} id="attach-stmt" className="rounded" />
              <label htmlFor="attach-stmt" className="text-xs text-blue-700 font-medium">Attach Vendor Statement PDF</label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending} className="bg-stone-900 hover:bg-stone-800" data-testid="send-vendor-email-btn">
            <Send size={14} className="mr-1" />
            {sending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Overview Tab ───────────────────────────────────────────────────────
function RecentTransactionsPinned({ vendorId }) {
  const token = localStorage.getItem('token');
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!vendorId) return;
    axios.get(`${API}/vendors/${vendorId}/transactions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        const list = (r.data?.transactions || [])
          .slice()
          .sort((a, b) => String(b.payment_date || b.created_at || '').localeCompare(String(a.payment_date || a.created_at || '')));
        setRows(list.slice(0, 5));
      })
      .catch(() => setRows([]));
  }, [vendorId, token]);

  if (!rows.length) return null;
  const fmt = (n) => `\u20B9${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4" data-testid="recent-transactions-pinned">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-stone-800 text-sm flex items-center gap-2">
          <Activity size={14} /> Recent Transactions
        </h3>
        <span className="text-[11px] text-stone-500">Last 5 · Newest first</span>
      </div>
      <div className="divide-y divide-stone-100">
        {rows.map((t, i) => {
          const isCredit = (t.credit || 0) > 0;
          return (
            <div key={t.id || i} className="flex items-center justify-between py-2" data-testid={`recent-txn-row-${i}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-stone-800 truncate">{t.transaction || t.notes || '-'}</p>
                <p className="text-[11px] text-stone-500">
                  {(t.payment_date || '').slice(0, 10)}
                  {t.project_name ? ` · ${t.project_name}` : ''}
                  {t.payment_mode ? ` · ${t.payment_mode}` : ''}
                </p>
              </div>
              <div className={`ml-3 text-sm font-semibold ${isCredit ? 'text-green-700' : 'text-stone-900'}`}>
                {isCredit ? '+' : ''}{fmt(t.amount || t.debit || t.credit || 0)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function OverviewTab({ data, vendorId }) {
  if (!data) return <div className="p-8 text-center text-stone-400">Loading...</div>;
  const { vendor, financials, projects_worked, monthly_expenses, recent_activity, performance_score } = data;
  const maxExpense = Math.max(...monthly_expenses.map(m => m.amount), 1);

  return (
    <div className="p-6 space-y-6" data-testid="overview-tab">
      {/* iter-386u-ab — Pinned Recent Transactions above the fold */}
      <RecentTransactionsPinned vendorId={vendorId} />
      {/* Top cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-stone-900 text-white rounded-lg p-4">
          <p className="text-xs text-stone-400 mb-1">Total Paid</p>
          <p className="text-2xl font-bold">{'\u20B9'}{financials.total_paid.toLocaleString('en-IN')}</p>
          <p className="text-xs text-stone-400 mt-1">{financials.payment_count} payment{financials.payment_count !== 1 ? 's' : ''}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
          <p className="text-xs text-blue-600 mb-1">Projects</p>
          <p className="text-2xl font-bold text-blue-900">{financials.projects_count}</p>
          <p className="text-xs text-blue-500 mt-1">Active associations</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
          <p className="text-xs text-green-600 mb-1">Quotes Submitted</p>
          <p className="text-2xl font-bold text-green-900">{data.recent_quotes?.length || 0}</p>
          <p className="text-xs text-green-500 mt-1">Total submissions</p>
        </div>
      </div>

      {/* Performance Score + Contact Details */}
      <div className="grid grid-cols-2 gap-6">
        <PerformanceScoreCard score={performance_score} />
        <div className="bg-white border border-stone-200 rounded-lg p-4">
          <h3 className="font-semibold text-stone-800 text-sm mb-3 flex items-center gap-2">
            <User size={14} /> Contact Details
          </h3>
          <div className="space-y-2.5 text-sm">
            <div className="flex items-center gap-2"><Mail size={13} className="text-stone-400" /><span className="text-stone-700">{vendor.email || '-'}</span></div>
            <div className="flex items-center gap-2"><Phone size={13} className="text-stone-400" /><span className="text-stone-700">{vendor.phone || 'Not provided'}</span></div>
            <div className="flex items-center gap-2"><Building2 size={13} className="text-stone-400" /><span className="text-stone-700">{vendor.company_name || 'Not provided'}</span></div>
            {vendor.address && <div className="flex items-start gap-2"><FileText size={13} className="text-stone-400 mt-0.5" /><span className="text-stone-700">{vendor.address}</span></div>}
          </div>
        </div>
      </div>

      {/* Other Details */}
      <div className="bg-white border border-stone-200 rounded-lg p-4">
        <h3 className="font-semibold text-stone-800 text-sm mb-3 flex items-center gap-2">
          <FileText size={14} /> Other Details
        </h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <div className="flex justify-between"><span className="text-stone-500">GSTIN</span><span className="font-medium text-stone-800">{vendor.gst_number || 'Not provided'}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">PAN</span><span className="font-medium text-stone-800">{vendor.pan_number || 'Not provided'}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Bank Details</span><span className="font-medium text-stone-800">{vendor.bank_details || 'Not provided'}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Since</span><span className="font-medium text-stone-800">{vendor.created_at ? formatDate(new Date(vendor.created_at), { shortMonth: true }) : '-'}</span></div>
        </div>
      </div>

      {/* Expenses Chart */}
      <div className="bg-white border border-stone-200 rounded-lg p-4">
        <h3 className="font-semibold text-stone-800 text-sm mb-4 flex items-center gap-2">
          <BarChart3 size={14} /> Monthly Expenses (Last 6 Months)
        </h3>
        <div className="flex items-end gap-3 h-32">
          {monthly_expenses.map((m, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-[10px] text-stone-500 font-medium">
                {m.amount > 0 ? `${'\u20B9'}${(m.amount / 1000).toFixed(0)}K` : ''}
              </span>
              <div
                className="w-full rounded-t transition-all"
                style={{ height: m.amount > 0 ? `${Math.max(8, (m.amount / maxExpense) * 100)}%` : '4px', backgroundColor: m.amount > 0 ? '#1c1917' : '#e7e5e4' }}
              />
              <span className="text-[10px] text-stone-500">{m.month.split(' ')[0]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Projects Worked */}
      {projects_worked.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-lg p-4">
          <h3 className="font-semibold text-stone-800 text-sm mb-3 flex items-center gap-2">
            <Briefcase size={14} /> Projects
          </h3>
          <div className="space-y-2">
            {projects_worked.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                <div>
                  <p className="text-sm font-medium text-stone-800">{p.project_name}</p>
                  <p className="text-xs text-stone-500">{p.payment_count} payment{p.payment_count !== 1 ? 's' : ''}</p>
                </div>
                <span className="font-semibold text-sm text-stone-900">{'\u20B9'}{p.total_paid.toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {recent_activity.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-lg p-4">
          <h3 className="font-semibold text-stone-800 text-sm mb-3 flex items-center gap-2">
            <Activity size={14} /> Recent Activity
          </h3>
          <div className="space-y-3">
            {recent_activity.map((a, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ArrowUpRight size={13} className="text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-stone-800">{a.description}</p>
                  <p className="text-xs text-stone-400">
                    {a.date ? formatDate(new Date(a.date), { shortMonth: true }) : '-'}
                    {a.payment_mode ? ` via ${a.payment_mode}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Transactions Tab ───────────────────────────────────────────────────
function TransactionsTab({ vendorId }) {
  const token = localStorage.getItem('token');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('all');
  const [projects, setProjects] = useState([]);

  useEffect(() => { fetchTransactions(); }, [vendorId]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/vendors/${vendorId}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(resp.data.transactions || []);
      const pMap = {};
      (resp.data.transactions || []).forEach(t => {
        if (t.project_id && !pMap[t.project_id]) pMap[t.project_id] = t.project_name;
      });
      setProjects(Object.entries(pMap).map(([id, name]) => ({ id, name })));
    } catch { toast.error('Failed to load transactions'); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const base = filterProject === 'all' ? transactions : transactions.filter(t => t.project_id === filterProject);
    // iter-386u-ab — default sort: Newest → Oldest.
    return base.slice().sort((a, b) =>
      String(b.payment_date || b.created_at || '').localeCompare(String(a.payment_date || a.created_at || ''))
    );
  }, [transactions, filterProject]);

  const filteredTotal = useMemo(() =>
    filtered.reduce((s, t) => s + (t.amount || 0), 0)
  , [filtered]);

  if (loading) return <div className="p-8 text-center text-stone-400">Loading transactions...</div>;

  return (
    <div className="p-6" data-testid="transactions-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-stone-800">All Transactions</h3>
          <p className="text-xs text-stone-500">{filtered.length} transactions | Total: {'\u20B9'}{filteredTotal.toLocaleString('en-IN')}</p>
        </div>
        {projects.length > 1 && (
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-48 h-8 text-xs"><SelectValue placeholder="Filter by project" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {filtered.length > 0 ? (
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-100 text-stone-600">
              <tr>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Project</th>
                <th className="p-3 text-left">Mode</th>
                <th className="p-3 text-left">Notes</th>
                <th className="p-3 text-right">Amount</th>
                <th className="p-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => (
                <tr key={t.id || i} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`txn-row-${i}`}>
                  <td className="p-3 text-stone-700">{(t.payment_date || t.created_at || '').slice(0, 10)}</td>
                  <td className="p-3 font-medium text-stone-800">{t.project_name || '-'}</td>
                  <td className="p-3 text-stone-600">
                    <span className="inline-flex items-center gap-1"><CreditCard size={12} />{t.payment_mode || '-'}</span>
                  </td>
                  <td className="p-3 text-stone-500 max-w-[200px] truncate">{t.notes || '-'}</td>
                  <td className="p-3 text-right font-bold text-stone-900">{'\u20B9'}{(t.amount || 0).toLocaleString('en-IN')}</td>
                  <td className="p-3 text-center">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px]">Paid</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-stone-800 text-white font-semibold">
                <td colSpan={4} className="p-3 text-right">Total:</td>
                <td className="p-3 text-right">{'\u20B9'}{filteredTotal.toLocaleString('en-IN')}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-lg p-10 text-center text-stone-400">
          <DollarSign size={32} className="mx-auto mb-2 text-stone-300" />
          No transactions found for this vendor.
        </div>
      )}
    </div>
  );
}

// ─── Ledger / Statement Tab ─────────────────────────────────────────────
function StatementTab({ vendorId, onEmailStatement }) {
  const token = localStorage.getItem('token');
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dateRange, setDateRange] = useState('all_time');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const getDateRange = (preset) => {
    const now = new Date();
    let s, e;
    switch (preset) {
      case 'this_month':
        s = new Date(now.getFullYear(), now.getMonth(), 1); e = now; break;
      case 'last_month':
        s = new Date(now.getFullYear(), now.getMonth() - 1, 1); e = new Date(now.getFullYear(), now.getMonth(), 0); break;
      case 'this_quarter':
        const qm = Math.floor(now.getMonth() / 3) * 3;
        s = new Date(now.getFullYear(), qm, 1); e = now; break;
      case 'this_year':
        s = new Date(now.getFullYear(), 0, 1); e = now; break;
      case 'all_time':
        s = new Date(2020, 0, 1); e = now; break;
      default: return null;
    }
    return { start: s.toISOString().slice(0, 10), end: e.toISOString().slice(0, 10) };
  };

  const fetchStatement = async (sd, ed) => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/vendors/${vendorId}/statement`, {
        params: { start_date: sd, end_date: ed },
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatement(resp.data);
    } catch { toast.error('Failed to load statement'); }
    finally { setLoading(false); }
  };

  const handleRangeChange = (preset) => {
    setDateRange(preset);
    if (preset !== 'custom') {
      const range = getDateRange(preset);
      if (range) { setStartDate(range.start); setEndDate(range.end); fetchStatement(range.start, range.end); }
    }
  };

  useEffect(() => { handleRangeChange('all_time'); }, [vendorId]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/vendors/${vendorId}/statement/pdf`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `vendor_statement_${startDate}_to_${endDate}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch { toast.error('Failed to download PDF'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="p-6" data-testid="statement-tab">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Select value={dateRange} onValueChange={handleRangeChange}>
          <SelectTrigger className="w-40 h-9 text-sm" data-testid="statement-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="this_month">This Month</SelectItem>
            <SelectItem value="last_month">Last Month</SelectItem>
            <SelectItem value="this_quarter">This Quarter</SelectItem>
            <SelectItem value="this_year">This Year</SelectItem>
            <SelectItem value="all_time">All Time</SelectItem>
            <SelectItem value="custom">Custom Range</SelectItem>
          </SelectContent>
        </Select>
        {dateRange === 'custom' && (
          <>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-36 h-9 text-sm" />
            <span className="text-stone-400">to</span>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-36 h-9 text-sm" />
            <Button size="sm" onClick={() => { if (startDate && endDate) fetchStatement(startDate, endDate); }} data-testid="fetch-statement-btn">Generate</Button>
          </>
        )}
        <div className="flex-1" />
        {statement && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadPDF} disabled={downloading} data-testid="download-statement-pdf">
              <Download size={14} className="mr-1" />
              {downloading ? 'Downloading...' : 'Download PDF'}
            </Button>
            {onEmailStatement && (
              <Button variant="outline" size="sm" onClick={onEmailStatement} data-testid="email-statement-btn">
                <Send size={14} className="mr-1" />
                Email Statement
              </Button>
            )}
          </div>
        )}
      </div>

      {loading && <div className="p-8 text-center text-stone-400">Loading statement...</div>}

      {statement && !loading && (
        <div className="space-y-6">
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="bg-stone-900 text-white px-5 py-3">
              <h3 className="font-semibold">Account Summary</h3>
              <p className="text-xs text-stone-400">{statement.period.start} to {statement.period.end}</p>
            </div>
            <div className="grid grid-cols-4 divide-x divide-stone-200">
              <div className="p-4 text-center">
                <p className="text-xs text-stone-500 mb-1">Opening Balance</p>
                <p className="text-lg font-bold text-stone-800">{'\u20B9'}{statement.opening_balance.toLocaleString('en-IN')}</p>
              </div>
              <div className="p-4 text-center">
                <p className="text-xs text-stone-500 mb-1">Paid in Period</p>
                <p className="text-lg font-bold text-red-700">{'\u20B9'}{statement.total_debits.toLocaleString('en-IN')}</p>
              </div>
              <div className="p-4 text-center">
                <p className="text-xs text-stone-500 mb-1">Credits / Returns</p>
                <p className="text-lg font-bold text-green-700">{'\u20B9'}{statement.total_credits.toLocaleString('en-IN')}</p>
              </div>
              <div className="p-4 text-center bg-stone-50">
                <p className="text-xs text-stone-500 mb-1">Closing Balance</p>
                <p className="text-lg font-bold text-stone-900">{'\u20B9'}{statement.closing_balance.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 text-stone-600">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-left">Mode</th>
                  <th className="p-3 text-right">Debit ({'\u20B9'})</th>
                  <th className="p-3 text-right">Credit ({'\u20B9'})</th>
                  <th className="p-3 text-right">Balance ({'\u20B9'})</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <td className="p-3 text-stone-500 italic" colSpan={3}>Opening Balance</td>
                  <td className="p-3 text-right">-</td>
                  <td className="p-3 text-right">-</td>
                  <td className="p-3 text-right font-semibold">{'\u20B9'}{statement.opening_balance.toLocaleString('en-IN')}</td>
                </tr>
                {statement.entries.map((e, i) => (
                  <tr key={i} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`ledger-row-${i}`}>
                    <td className="p-3 text-stone-700 whitespace-nowrap">{(e.date || '').slice(0, 10)}</td>
                    <td className="p-3 font-medium text-stone-800">{e.description}</td>
                    <td className="p-3 text-stone-500">{e.payment_mode || '-'}</td>
                    <td className="p-3 text-right text-red-700 font-medium">{e.debit > 0 ? `${'\u20B9'}${e.debit.toLocaleString('en-IN')}` : '-'}</td>
                    <td className="p-3 text-right text-green-700 font-medium">{e.credit > 0 ? `${'\u20B9'}${e.credit.toLocaleString('en-IN')}` : '-'}</td>
                    <td className="p-3 text-right font-semibold text-stone-900">{'\u20B9'}{e.balance.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
                {statement.entries.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-stone-400">No transactions in this period</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-stone-800 text-white font-semibold">
                  <td colSpan={3} className="p-3 text-right">Closing Balance:</td>
                  <td className="p-3 text-right">{'\u20B9'}{statement.total_debits.toLocaleString('en-IN')}</td>
                  <td className="p-3 text-right">{'\u20B9'}{statement.total_credits.toLocaleString('en-IN')}</td>
                  <td className="p-3 text-right">{'\u20B9'}{statement.closing_balance.toLocaleString('en-IN')}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────
export default function VendorDetail() {
  const { vendorId: urlVendorId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState(urlVendorId || null);
  const [overview, setOverview] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailStatementOpen, setEmailStatementOpen] = useState(false);

  useEffect(() => { fetchVendors(); }, []);

  useEffect(() => {
    if (selectedVendorId) {
      fetchOverview(selectedVendorId);
      navigate(`/vendors/${selectedVendorId}`, { replace: true });
    }
  }, [selectedVendorId]);

  const fetchVendors = async () => {
    try {
      const resp = await axios.get(`${API}/vendors/directory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(resp.data || []);
      if (!selectedVendorId && resp.data.length > 0) {
        setSelectedVendorId(urlVendorId || resp.data[0].id);
      }
    } catch { toast.error('Failed to load vendors'); }
    finally { setLoading(false); }
  };

  const fetchOverview = async (vid) => {
    try {
      const resp = await axios.get(`${API}/vendors/${vid}/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(resp.data);
    } catch { setOverview(null); }
  };

  const handleSelectVendor = (vid) => {
    setSelectedVendorId(vid);
    setActiveTab('overview');
    setOverview(null);
  };

  const handleVendorUpdated = () => {
    fetchVendors();
    if (selectedVendorId) fetchOverview(selectedVendorId);
  };

  const handleMarkInactive = async () => {
    if (!selectedVendorId) return;
    const status = overview?.vendor?.status === 'inactive' ? 'active' : 'inactive';
    try {
      await axios.patch(`${API}/vendors/${selectedVendorId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Vendor marked as ${status}`);
      handleVendorUpdated();
    } catch { toast.error('Failed to update status'); }
  };

  const handleDeleteVendor = async () => {
    if (!selectedVendorId || !confirm('Are you sure you want to delete this vendor? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/vendors/${selectedVendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendor deleted');
      setSelectedVendorId(null);
      setOverview(null);
      fetchVendors();
    } catch { toast.error('Failed to delete vendor'); }
  };

  const handleRecordPayment = () => {
    // Redirect to payments page with vendor pre-selected
    navigate(`/payments?vendor_id=${selectedVendorId}&vendor_name=${encodeURIComponent(overview?.vendor?.name || '')}`);
  };

  const handleCreatePO = () => {
    navigate(`/purchase-orders?vendor_id=${selectedVendorId}&vendor_name=${encodeURIComponent(overview?.vendor?.name || '')}`);
  };

  const handleEmailVendor = () => {
    if (overview?.vendor?.email) {
      setEmailDialogOpen(true);
    } else {
      toast.error('No email address for this vendor');
    }
  };

  const handleEmailStatement = () => {
    if (overview?.vendor?.email) {
      setEmailStatementOpen(true);
    } else {
      toast.error('No email address for this vendor');
    }
  };

  const handleViewCatalogues = () => {
    navigate('/vendor-catalogues');
  };

  const tabs = [
    { key: 'overview', label: 'Overview', icon: BarChart3 },
    { key: 'transactions', label: 'Transactions', icon: DollarSign },
    { key: 'statement', label: 'Ledger / Statement', icon: FileText },
  ];

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-[calc(100vh-64px)] bg-stone-50"><p className="text-stone-400">Loading vendor directory...</p></div></Layout>;
  }

  return (
    <Layout>
    <div className="flex h-[calc(100vh-64px)] bg-stone-50" data-testid="vendor-detail-page">
      <Toaster position="top-right" richColors />

      <VendorList
        vendors={vendors}
        selectedId={selectedVendorId}
        onSelect={handleSelectVendor}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onCompare={() => navigate('/vendors/compare')}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedVendorId && overview ? (
          <>
            {/* Vendor Header with Action Buttons */}
            <div className="bg-white border-b border-stone-200 px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-stone-900" data-testid="vendor-detail-name">{overview.vendor.name}</h2>
                    {overview.vendor.status === 'inactive' && (
                      <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-[10px]">Inactive</Badge>
                    )}
                  </div>
                  <p className="text-sm text-stone-500">{overview.vendor.company_name || overview.vendor.email}</p>
                </div>

                {/* Action Buttons — Zoho-style */}
                <div className="flex items-center gap-2" data-testid="vendor-action-buttons">
                  {/* Edit */}
                  <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-vendor-btn">
                    <Pencil size={14} className="mr-1" /> Edit
                  </Button>

                  {/* Attachment / Catalogues */}
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleViewCatalogues} title="Vendor Catalogues" data-testid="vendor-attachment-btn">
                    <Paperclip size={14} />
                  </Button>

                  {/* New Transaction Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid="new-transaction-btn">
                        <Plus size={14} className="mr-1" /> New Transaction <ChevronDown size={12} className="ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuLabel className="text-[10px] text-stone-500 uppercase tracking-wide">Payments</DropdownMenuLabel>
                      <DropdownMenuItem onClick={handleRecordPayment} data-testid="action-record-payment">
                        <DollarSign size={14} className="mr-2" /> Record Payment
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] text-stone-500 uppercase tracking-wide">Orders</DropdownMenuLabel>
                      <DropdownMenuItem onClick={handleCreatePO} data-testid="action-create-po">
                        <ShoppingCart size={14} className="mr-2" /> Purchase Order
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* More Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="more-actions-btn">
                        More <ChevronDown size={12} className="ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={handleEmailVendor} data-testid="action-email-vendor">
                        <Send size={14} className="mr-2" /> Email Vendor
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleViewCatalogues} data-testid="action-view-catalogues">
                        <Paperclip size={14} className="mr-2" /> View Catalogues
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleMarkInactive} data-testid="action-toggle-status">
                        <Ban size={14} className="mr-2" />
                        {overview.vendor.status === 'inactive' ? 'Mark as Active' : 'Mark as Inactive'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDeleteVendor} className="text-red-600 focus:text-red-600" data-testid="action-delete-vendor">
                        <Trash2 size={14} className="mr-2" /> Delete Vendor
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 mt-4 border-b border-stone-200 -mb-[17px]">
                {tabs.map(t => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setActiveTab(t.key)}
                      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === t.key
                          ? 'border-stone-900 text-stone-900'
                          : 'border-transparent text-stone-500 hover:text-stone-700'
                      }`}
                      data-testid={`tab-${t.key}`}
                    >
                      <Icon size={14} />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto">
              {activeTab === 'overview' && <OverviewTab data={overview} vendorId={selectedVendorId} />}
              {activeTab === 'transactions' && <TransactionsTab vendorId={selectedVendorId} />}
              {activeTab === 'statement' && <StatementTab vendorId={selectedVendorId} onEmailStatement={handleEmailStatement} />}
            </div>

            {/* Edit Dialog */}
            <EditVendorDialog
              open={editOpen}
              onClose={() => setEditOpen(false)}
              vendor={overview?.vendor}
              onSaved={handleVendorUpdated}
            />
            {/* Email Vendor Dialog (general) */}
            <VendorEmailDialog
              open={emailDialogOpen}
              onClose={() => setEmailDialogOpen(false)}
              vendor={overview?.vendor}
              mode="general"
            />
            {/* Email Statement Dialog */}
            <VendorEmailDialog
              open={emailStatementOpen}
              onClose={() => setEmailStatementOpen(false)}
              vendor={overview?.vendor}
              mode="statement"
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-stone-400">
              <User size={48} className="mx-auto mb-3 text-stone-300" />
              <p className="font-medium">Select a vendor to view details</p>
              <p className="text-sm mt-1">Choose from the list on the left</p>
            </div>
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
}
